import React, { useState, useEffect, useRef } from "react";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { 
  Users, 
  Upload, 
  Trash2, 
  Search, 
  CheckCircle2, 
  AlertCircle, 
  FileText, 
  Loader2, 
  UserPlus,
  Mail,
  Download
} from "lucide-react";
import { toast } from "sonner";
import { useApiClient } from "@/hooks/use-api-client";

interface MarketingContact {
  id: string;
  email: string;
  full_name: string;
  source: string;
  status: string;
  created_at: string;
}

interface MarketingContactsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onContactsUpdated?: () => void;
}

export function MarketingContactsModal({
  isOpen,
  onClose,
  onContactsUpdated,
}: MarketingContactsModalProps) {
  const apiClient = useApiClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [contacts, setContacts] = useState<MarketingContact[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [manualName, setManualName] = useState("");
  const [manualEmail, setManualEmail] = useState("");
  const [activeTab, setActiveTab] = useState<"list" | "add">("list");

  // Fetch contacts
  const fetchContacts = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/api/v1/bulletins/marketing/contacts");
      const contactList = res.ok ? res.data?.contacts : (res.contacts || []);
      if (contactList) {
        setContacts(contactList);
      }
    } catch (err: any) {
      console.error("Error fetching marketing contacts:", err);
      toast.error("No se pudo cargar la lista de contactos.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchContacts();
    }
  }, [isOpen]);

  // Handle CSV file upload & parsing
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    const reader = new FileReader();

    reader.onload = async (event) => {
      try {
        const text = event.target?.result as string;
        const lines = text.split(/\r\n|\n/);
        
        if (lines.length < 2) {
          toast.error("El archivo CSV no contiene suficientes filas.");
          setIsUploading(false);
          return;
        }

        // Parse header line to detect email & name column index
        const headers = lines[0].split(/[,;\t]/).map((h) => h.trim().toLowerCase());
        
        let emailIdx = headers.findIndex((h) => h.includes("correo") || h.includes("email") || h.includes("mail"));
        let nameIdx = headers.findIndex((h) => h.includes("nombre") || h.includes("name") || h.includes("contacto") || h.includes("full_name"));

        if (emailIdx === -1) emailIdx = 1; // Default fallback index
        if (nameIdx === -1) nameIdx = 0;

        const contactMap = new Map<string, { email: string; full_name: string }>();

        for (let i = 1; i < lines.length; i++) {
          const rowStr = lines[i].trim();
          if (!rowStr) continue;

          const cols = rowStr.split(/[,;\t]/).map((c) => c.replace(/^["']|["']$/g, "").trim());
          const email = (cols[emailIdx] || cols.find((c) => c.includes("@")) || "").toLowerCase();
          const name = cols[nameIdx] || "Estimado(a) Colega";

          if (email && email.includes("@") && email.includes(".")) {
            if (!contactMap.has(email) || (name !== "Estimado(a) Colega" && contactMap.get(email)?.full_name === "Estimado(a) Colega")) {
              contactMap.set(email, { email, full_name: name });
            }
          }
        }

        const parsedContacts = Array.from(contactMap.values());

        if (parsedContacts.length === 0) {
          toast.error("No se encontraron correos válidos en el archivo subido.");
          setIsUploading(false);
          return;
        }

        // Send payload to backend
        const res = await apiClient.post("/api/v1/bulletins/marketing/import-contacts", {
          contacts: parsedContacts,
        });

        if (res.ok) {
          toast.success(res.data?.message || `Se importaron ${parsedContacts.length} contactos.`);
          fetchContacts();
          if (onContactsUpdated) onContactsUpdated();
          setActiveTab("list");
        } else {
          toast.error(res.data?.detail || "No se pudieron importar los contactos.");
        }
      } catch (err: any) {
        console.error("Error processing CSV:", err);
        toast.error("Error al procesar el archivo CSV.");
      } finally {
        setIsUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    };

    reader.readAsText(file);
  };

  // Add single manual contact
  const handleAddSingleContact = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!manualEmail || !manualEmail.includes("@")) {
      toast.error("Ingresa un correo electrónico válido.");
      return;
    }

    setIsUploading(true);
    try {
      const res = await apiClient.post("/api/v1/bulletins/marketing/import-contacts", {
        contacts: [{ email: manualEmail.toLowerCase(), full_name: manualName || "Estimado(a) Colega" }],
      });

      if (res.ok) {
        toast.success("Contacto agregado a la lista de difusión.");
        setManualEmail("");
        setManualName("");
        fetchContacts();
        if (onContactsUpdated) onContactsUpdated();
        setActiveTab("list");
      } else {
        toast.error(res.data?.detail || "No se pudo agregar el contacto.");
      }
    } catch (err: any) {
      console.error("Error adding contact:", err);
      toast.error("No se pudo agregar el contacto.");
    } finally {
      setIsUploading(false);
    }
  };

  // Delete contact
  const handleDeleteContact = async (id: string, email: string) => {
    try {
      const res = await apiClient.delete(`/api/v1/bulletins/marketing/contacts/${id}`);
      if (res.ok) {
        setContacts((prev) => prev.filter((c) => c.id !== id));
        toast.success(`Contacto ${email} eliminado.`);
        if (onContactsUpdated) onContactsUpdated();
      } else {
        toast.error("No se pudo eliminar el contacto.");
      }
    } catch (err: any) {
      console.error("Error deleting contact:", err);
      toast.error("Error al eliminar el contacto.");
    }
  };

  // Download template CSV
  const handleDownloadTemplate = () => {
    const csvContent = "Nombre,Correo\nJuan Pérez,juan.perez@ejemplo.com\nMaría Rodríguez,maria.rodriguez@firma.co";
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "plantilla_difusion_prospectos.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredContacts = contacts.filter(
    (c) =>
      c.email.toLowerCase().includes(search.toLowerCase()) ||
      (c.full_name && c.full_name.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl rounded-2xl p-0 overflow-hidden bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 shadow-2xl">
        <DialogHeader className="p-6 pb-4 bg-slate-50 dark:bg-slate-800/40 border-b border-slate-100 dark:border-slate-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-pink-100 dark:bg-pink-900/40 flex items-center justify-center text-pink-600 dark:text-pink-400">
                <Users className="h-5 w-5" />
              </div>
              <div>
                <DialogTitle className="text-lg font-bold text-slate-900 dark:text-white">
                  Lista de Difusión de Marketing
                </DialogTitle>
                <DialogDescription className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  Gestiona prospectos y contactos externos para campañas persuasivas por correo.
                </DialogDescription>
              </div>
            </div>
            <Badge variant="outline" className="bg-pink-50 dark:bg-pink-950/50 text-pink-700 dark:text-pink-300 border-pink-200 dark:border-pink-800 font-bold px-3 py-1 text-xs">
              {contacts.length} Contactos Activos
            </Badge>
          </div>

          {/* Navigation Tabs */}
          <div className="flex gap-2 mt-4 pt-2 border-t border-slate-200/60 dark:border-slate-700/60">
            <Button
              variant={activeTab === "list" ? "default" : "outline"}
              size="sm"
              onClick={() => setActiveTab("list")}
              className="rounded-xl text-xs font-bold"
            >
              <Users className="h-3.5 w-3.5 mr-1.5" />
              Ver Contactos ({contacts.length})
            </Button>
            <Button
              variant={activeTab === "add" ? "default" : "outline"}
              size="sm"
              onClick={() => setActiveTab("add")}
              className="rounded-xl text-xs font-bold"
            >
              <UserPlus className="h-3.5 w-3.5 mr-1.5" />
              Importar / Agregar
            </Button>
          </div>
        </DialogHeader>

        <div className="p-6">
          {activeTab === "list" ? (
            <div className="space-y-4">
              {/* Search Bar */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Buscar por nombre o correo..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 rounded-xl bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 text-sm"
                />
              </div>

              {/* Table / List */}
              {loading ? (
                <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                  <Loader2 className="h-8 w-8 animate-spin text-pink-500 mb-2" />
                  <span className="text-xs font-medium">Cargando lista de contactos...</span>
                </div>
              ) : filteredContacts.length === 0 ? (
                <div className="text-center py-12 bg-slate-50 dark:bg-slate-800/20 rounded-2xl border border-dashed border-slate-200 dark:border-slate-800">
                  <Users className="h-10 w-10 mx-auto text-slate-300 dark:text-slate-600 mb-2" />
                  <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                    No se encontraron contactos
                  </p>
                  <p className="text-xs text-slate-400 max-w-sm mx-auto mt-1">
                    {search
                      ? "Intenta con otro término de búsqueda."
                      : "Importa un archivo CSV con tus prospectos para comenzar a enviarles boletines."}
                  </p>
                  {!search && (
                    <Button
                      onClick={() => setActiveTab("add")}
                      className="mt-4 bg-pink-600 hover:bg-pink-700 text-white font-bold rounded-xl text-xs px-4"
                    >
                      <Upload className="h-3.5 w-3.5 mr-1.5" /> Importar Contactos CSV
                    </Button>
                  )}
                </div>
              ) : (
                <div className="max-h-[320px] overflow-y-auto custom-scrollbar border border-slate-200 dark:border-slate-800 rounded-xl divide-y divide-slate-100 dark:divide-slate-800">
                  {filteredContacts.map((c) => (
                    <div
                      key={c.id}
                      className="flex items-center justify-between p-3 px-4 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center font-bold text-xs text-slate-600 dark:text-slate-300">
                          {c.full_name ? c.full_name.charAt(0).toUpperCase() : "C"}
                        </div>
                        <div>
                          <p className="text-xs font-bold text-slate-800 dark:text-slate-200">
                            {c.full_name || "Estimado(a) Colega"}
                          </p>
                          <p className="text-[11px] text-slate-500 dark:text-slate-400 font-mono">
                            {c.email}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <Badge
                          variant="secondary"
                          className="bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800 text-[10px] font-semibold"
                        >
                          Activo
                        </Badge>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteContact(c.id, c.email)}
                          className="h-7 w-7 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors"
                          title="Eliminar contacto"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-6">
              {/* Option A: CSV Upload */}
              <div className="p-5 rounded-2xl bg-gradient-to-br from-pink-50 to-purple-50 dark:from-pink-950/20 dark:to-purple-950/20 border border-pink-200/70 dark:border-pink-800/50">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
                      <FileText className="h-4 w-4 text-pink-600 dark:text-pink-400" />
                      Carga Masiva de Archivo CSV / Excel
                    </h4>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      Sube un archivo con columnas <strong>Nombre</strong> y <strong>Correo</strong>.
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleDownloadTemplate}
                    className="text-xs text-pink-600 dark:text-pink-400 font-bold hover:bg-pink-100/50 dark:hover:bg-pink-900/50 rounded-xl"
                  >
                    <Download className="h-3.5 w-3.5 mr-1" /> Descargar Plantilla
                  </Button>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept=".csv, .txt, .tsv"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="csv-file-input"
                  />
                  <label htmlFor="csv-file-input" className="w-full">
                    <Button
                      type="button"
                      disabled={isUploading}
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full bg-pink-600 hover:bg-pink-700 text-white font-bold rounded-xl py-6 text-xs flex items-center justify-center gap-2 shadow-md shadow-pink-500/20"
                    >
                      {isUploading ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" /> Procesando e importando...
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4" /> Seleccionar Archivo CSV de Contactos
                        </>
                      )}
                    </Button>
                  </label>
                </div>
              </div>

              {/* Option B: Manual Single Contact Add */}
              <form onSubmit={handleAddSingleContact} className="space-y-4 border-t border-slate-100 dark:border-slate-800 pt-4">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                  <UserPlus className="h-3.5 w-3.5" /> O agregar contacto individualmente
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] font-bold text-slate-600 dark:text-slate-400 block mb-1">
                      Nombre Completo
                    </label>
                    <Input
                      placeholder="Ej: Dr. Fernando Gómez"
                      value={manualName}
                      onChange={(e) => setManualName(e.target.value)}
                      className="rounded-xl text-xs bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-bold text-slate-600 dark:text-slate-400 block mb-1">
                      Correo Electrónico *
                    </label>
                    <Input
                      type="email"
                      required
                      placeholder="ejemplo@abogados.co"
                      value={manualEmail}
                      onChange={(e) => setManualEmail(e.target.value)}
                      className="rounded-xl text-xs bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isUploading}
                  variant="outline"
                  className="w-full rounded-xl text-xs font-bold border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1.5 text-emerald-500" />
                  Agregar Contacto a la Lista
                </Button>
              </form>
            </div>
          )}
        </div>

        <DialogFooter className="p-4 px-6 bg-slate-50 dark:bg-slate-800/40 border-t border-slate-100 dark:border-slate-800 flex justify-between items-center">
          <span className="text-[11px] text-slate-400">
            Los contactos de esta lista recibirán las publicaciones enviadas a la audiencia "Lista de Difusión".
          </span>
          <Button
            variant="outline"
            onClick={onClose}
            className="rounded-xl px-5 text-xs font-bold border-slate-200 dark:border-slate-700"
          >
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
